<template>
    <div class="dashboard-container">
        <!-- Header -->
        <header class="dashboard-header">
            <div class="header-content" style="display: flex; align-items: center;">
                <button class="back-button" @click="$router.push('/')">
                    <span>←</span>
                </button>
                <div class="header-title" style="margin-left: 16px;">
                    <span class="page-title"> {{ $t("dashboards.dashboards") }}</span>
                </div>
                <div style="flex: 1;"></div>
                <div class="d-flex ga-2">
                    <!-- Table View Button - Only visible on small screens -->
                    <v-btn 
                        color="primary" 
                        :class="screenWidth < 600 ? 'px-2 min-w-0' : 'text-white'"
                        :icon="screenWidth < 600"
                        @click="openTableView"
                    >
                        <v-icon v-if="screenWidth < 600">mdi-table</v-icon>
                        <template v-else>
                            <v-icon start>mdi-table</v-icon>
                            {{ $t("dashboards.tableView") }}
                        </template>
                    </v-btn>

                    <!-- Export Button -->
                    <v-btn 
                        color="black" 
                        :class="screenWidth < 600 ? 'px-2 min-w-0' : 'text-white'"
                        :icon="screenWidth < 600"
                        @click="showPasswordDialog = true"
                    >
                        <v-icon v-if="screenWidth < 600">mdi-export</v-icon>
                        <template v-else>
                            <v-icon start>mdi-export</v-icon>
                            {{ $t("dashboards.export") }}
                        </template>
                    </v-btn>
                </div>
            </div>
        </header>

        <!-- Navigation Tabs Section -->
        <div class="nav-section">
            <nav class="nav-tabs-container">
                <div class="nav-tabs">
                    <button v-for="tab in tabs" :key="tab.id" :class="['nav-tab', { active: activeTab === tab.id }]"
                        @click="setTab(tab.id)">
                        {{ $t(tab.name) }}
                    </button>
                </div>
            </nav>
        </div>

        <div class="dashboard-content">
            <!-- Conditionally render different dashboard components based on active tab -->
            <RealEstateOwners v-if="activeTab === 1" :screen-width="screenWidth" />
            <RentersTenants v-else-if="activeTab === 2" :screen-width="screenWidth" />
            <FacilityManagement v-else-if="activeTab === 3" :screen-width="screenWidth" />
            <AssetManagers v-else-if="activeTab === 4" :screen-width="screenWidth" />
        </div>

        <!-- Password Dialog -->
        <v-dialog v-model="showPasswordDialog" max-width="400">
            <v-card>
                <v-card-title>{{ $t("dashboards.enterPasswordToExport") }}</v-card-title>
                <v-card-text>
                    <v-text-field v-model="enteredPassword" :label="$t('dashboards.password')" type="password"
                        :error-messages="errorMessage"></v-text-field>
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn color="grey" text @click="closeDialog">{{ $t("dashboards.cancel") }}</v-btn>
                    <v-btn color="primary" text @click="validatePassword">{{ $t("dashboards.submit") }}</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- Table View Dialog - Component based on active tab -->
        <RealEstateDetailedView 
            v-if="activeTab === 1"
            v-model="showTableView"
        />
        <RentersTenantsDetailedView 
            v-if="activeTab === 2" 
            v-model="showTableView" 
        />
        <!-- Add similar components for other tabs when needed -->
        <!-- <RentersTenantsDetailedView v-if="activeTab === 2" v-model="showTableView" /> -->
        <!-- <FacilityManagementDetailedView v-if="activeTab === 3" v-model="showTableView" /> -->
        <!-- <AssetManagersDetailedView v-if="activeTab === 4" v-model="showTableView" /> -->
    </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from "vue";
import { VBtn, VDialog, VCard, VCardTitle, VCardText, VCardActions, VTextField, VSpacer, VIcon } from 'vuetify/components';
import RealEstateOwners from "@/components/OneVrAnalytics/AnalyticsDashboardStyles/AnalyticsDashboardChilds/RealEstateOwners.vue";
import RentersTenants from "@/components/OneVrAnalytics/AnalyticsDashboardStyles/AnalyticsDashboardChilds/RentersTenants.vue";
import FacilityManagement from "@/components/OneVrAnalytics/AnalyticsDashboardStyles/AnalyticsDashboardChilds/FacilityManagement.vue";
import AssetManagers from "@/components/OneVrAnalytics/AnalyticsDashboardStyles/AnalyticsDashboardChilds/AssetManagers.vue";
import RealEstateDetailedView from "@/components/OneVrAnalytics/AnalyticsDashboardStyles/AnalyticsDashboardChilds/RealEstateDetailedView.vue";
import RentersTenantsDetailedView from "@/components/OneVrAnalytics/AnalyticsDashboardStyles/AnalyticsDashboardChilds/RentersTenantsDetailedView.vue";

import { exportRealEstateOwnersToExcel } from "./OneVrAnalytics/AnalyticsDashboardStyles/AnalyticsDashboardChilds/exportRealEstateOwners";
import { exportRentersTenantsDashboardToExcel } from "./OneVrAnalytics/AnalyticsDashboardStyles/AnalyticsDashboardChilds/exportRentersData.js";
import { exportFacilityManagementToExcel } from "./OneVrAnalytics/AnalyticsDashboardStyles/AnalyticsDashboardChilds/exportFacilityData.js";
import { exportAssetManagersToExcel } from "./OneVrAnalytics/AnalyticsDashboardStyles/AnalyticsDashboardChilds/exportAssetManagersData.js";


const activeTab = ref(1);
const screenWidth = ref(window.innerWidth);
const screenHeight = ref(window.innerHeight);
const showPasswordDialog = ref(false);
const showTableView = ref(false);
const enteredPassword = ref('');
const errorMessage = ref('');

const tabHashes = {
    1: 'realestateowners',
    2: 'renterstenants',
    3: 'facilitymanagement',
    4: 'assetmanagers'
};

const hashToTab = Object.fromEntries(
    Object.entries(tabHashes).map(([id, hash]) => [hash, parseInt(id)])
);

function updateScreenSize() {
    screenWidth.value = window.innerWidth;
    screenHeight.value = window.innerHeight;
}

function handleHashChange() {
    const hash = window.location.hash.substring(1); // Remove '#'
    const tabId = hashToTab[hash];
    if (tabId) {
        activeTab.value = tabId;
    } else {
        activeTab.value = 1;
        updateHash();
    }
}

function updateHash() {
    const hashValue = tabHashes[activeTab.value];
    if (hashValue && window.location.hash !== `#${hashValue}`) {
        window.location.hash = `#${hashValue}`;
    }
}

function setTab(id) {
    activeTab.value = id;
    updateHash();
}

function closeDialog() {
    showPasswordDialog.value = false;
    enteredPassword.value = '';
    errorMessage.value = '';
}

function validatePassword() {
    if (enteredPassword.value === 'Pinestack#2025') {
        closeDialog();
        performExport();
    } else {
        errorMessage.value = 'Incorrect password';
    }
}

function performExport() {
    if (activeTab.value === 1) {
        exportRealEstateOwnersToExcel();
    } else if (activeTab.value === 2) {
        exportRentersTenantsDashboardToExcel();
    } else if (activeTab.value === 3) {
        exportFacilityManagementToExcel();
    } else if (activeTab.value === 4) {
        exportAssetManagersToExcel();
    }
}

function openTableView() {
    showTableView.value = true;
}

onMounted(() => {
    window.addEventListener("resize", updateScreenSize);
    window.addEventListener("hashchange", handleHashChange);
    handleHashChange(); // Initial setup
});

onUnmounted(() => {
    window.removeEventListener("resize", updateScreenSize);
    window.removeEventListener("hashchange", handleHashChange);
});

const tabs = ref([
    { id: 1, name: "dashboards.real-estate" },
    { id: 2, name: "dashboards.renter" },
    { id: 3, name: "dashboards.FM" },
    { id: 4, name: "dashboards.AM" },
]);
</script>

<style scoped>
@import "@/components/OneVrAnalytics/AnalyticsDashboardStyles/ClientSegmentAnalytics.css";

.dashboard-container {
    height: 100vh;
    height: 100dvh;
    display: flex;
    flex-direction: column;
    padding-top: env(safe-area-inset-top, 0px);
    padding-bottom: env(safe-area-inset-bottom, 0px);
    padding-left: env(safe-area-inset-left, 0px);
    padding-right: env(safe-area-inset-right, 0px);
    box-sizing: border-box;
}

.dashboard-header {
    flex-shrink: 0;
}

.nav-section {
    flex-shrink: 0;
}

.dashboard-content {
    flex: 1;
    overflow: auto;
}

/* Ensure icon buttons have proper size on mobile */
@media (max-width: 599px) {
    .v-btn.min-w-0 {
        min-width: 40px !important;
        width: 40px;
        height: 40px;
    }
}
</style>